import { useEffect, useState } from 'react';
import {
  Search,
  Layers,
  Clock,
  Trash2,
  Plus,
  Tag,
  LayoutGrid,
  List,
} from 'lucide-react';
import { useTemplateStore } from '../../stores/templateStore';
import { useStudioStore } from '../../stores/studioStore';
import { TEMPLATE_CATEGORIES, type TemplateCategory, type Template } from '../../types';

interface TemplateGalleryProps {
  onCreateFromTemplate?: (template: Template) => void;
}

export default function TemplateGallery({ onCreateFromTemplate }: TemplateGalleryProps) {
  const {
    templates,
    isLoading,
    searchQuery,
    categoryFilter,
    loadTemplates,
    setSearchQuery,
    setCategoryFilter,
    deleteTemplate,
    incrementUsage,
  } = useTemplateStore();

  const { setActiveProject, createProject } = useStudioStore();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates, categoryFilter]);

  const filtered = templates.filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      (t as any).tags?.some((tag: string) => tag.toLowerCase().includes(q))
    );
  });

  const handleUseTemplate = async (template: Template) => {
    const { loadTemplate } = useTemplateStore.getState();
    const fullTemplate = await loadTemplate(template.id);
    if (!fullTemplate) return;

    const dims = fullTemplate.canvas_state?.dimensions || { width: 1200, height: 400 };
    const project = await createProject(
      `From: ${fullTemplate.name}`,
      fullTemplate.description || '',
      dims
    );
    if (project) {
      const { updateProject } = useStudioStore.getState();
      await updateProject(project.id, {
        canvas_state: fullTemplate.canvas_state,
      });
      useStudioStore.getState().setActiveProject({
        ...project,
        canvas_state: fullTemplate.canvas_state,
      });
      incrementUsage(fullTemplate.id);
      onCreateFromTemplate?.(fullTemplate);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteTemplate(id);
    setDeleteConfirmId(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500/30 transition-colors"
          />
        </div>
        <div className="flex bg-white/[0.03] border border-white/[0.06] rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-white/[0.06] text-white/70' : 'text-white/30 hover:text-white/50'}`}
          >
            <LayoutGrid size={14} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-white/[0.06] text-white/70' : 'text-white/30 hover:text-white/50'}`}
          >
            <List size={14} />
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        <button
          onClick={() => setCategoryFilter(null)}
          className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
            !categoryFilter
              ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
              : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:text-white/60'
          }`}
        >
          All
        </button>
        {TEMPLATE_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategoryFilter(cat.id)}
            className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
              categoryFilter === cat.id
                ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:text-white/60'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 gap-4' : 'space-y-2'}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`rounded-xl bg-zinc-900/50 border border-white/[0.04] animate-pulse ${
                viewMode === 'grid' ? 'h-44' : 'h-16'
              }`}
            />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {filtered.map((template) => (
              <div
                key={template.id}
                className="group relative p-4 rounded-xl bg-zinc-900/50 border border-white/[0.06] hover:border-cyan-500/20 hover:bg-zinc-900 transition-all"
              >
                <div className="w-full h-24 rounded-lg bg-zinc-800/50 border border-white/[0.04] mb-3 flex items-center justify-center">
                  <Layers size={20} className="text-zinc-700" />
                </div>

                <h3 className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors truncate">
                  {template.name}
                </h3>

                {template.description && (
                  <p className="text-[11px] text-white/25 mt-0.5 line-clamp-1">{template.description}</p>
                )}

                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] font-mono text-zinc-600">v{template.version}</span>
                  <span className="text-zinc-700">&middot;</span>
                  <span className="text-[11px] text-zinc-600 flex items-center gap-1">
                    <Clock size={10} />
                    {new Date(template.updated_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 mt-2">
                  <span
                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
                      template.is_published
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-zinc-500/10 text-zinc-500'
                    }`}
                  >
                    {template.is_published ? 'Published' : 'Draft'}
                  </span>
                  {(template as any).category && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400">
                      {(template as any).category}
                    </span>
                  )}
                </div>

                {(template as any).tags?.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {(template as any).tags.slice(0, 3).map((tag: string) => (
                      <span
                        key={tag}
                        className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 bg-white/[0.04] text-white/30 rounded-full"
                      >
                        <Tag size={8} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button
                    onClick={() => handleUseTemplate(template)}
                    className="p-1.5 rounded-lg bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25 transition-colors"
                    title="Create project from template"
                  >
                    <Plus size={14} />
                  </button>
                  {deleteConfirmId === template.id ? (
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-[10px] font-medium px-2"
                    >
                      Confirm
                    </button>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(template.id)}
                      className="p-1.5 rounded-lg bg-white/[0.04] text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Delete template"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1.5">
            {filtered.map((template) => (
              <div
                key={template.id}
                className="group flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-white/[0.06] hover:border-cyan-500/20 hover:bg-zinc-900 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-zinc-800/50 border border-white/[0.04] flex items-center justify-center flex-shrink-0">
                  <Layers size={14} className="text-zinc-700" />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-zinc-300 truncate">{template.name}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-mono text-zinc-600">v{template.version}</span>
                    <span className="text-zinc-700">&middot;</span>
                    <span className="text-[10px] text-zinc-600">
                      {new Date(template.updated_at).toLocaleDateString()}
                    </span>
                    {(template as any).category && (
                      <>
                        <span className="text-zinc-700">&middot;</span>
                        <span className="text-[10px] text-blue-400">{(template as any).category}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleUseTemplate(template)}
                    className="p-1.5 rounded-lg bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25 transition-colors"
                    title="Create project from template"
                  >
                    <Plus size={14} />
                  </button>
                  {deleteConfirmId === template.id ? (
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-[10px] font-medium px-2"
                    >
                      Confirm
                    </button>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(template.id)}
                      className="p-1.5 rounded-lg bg-white/[0.04] text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Delete template"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="p-12 rounded-xl bg-zinc-900/30 border border-white/[0.04] text-center">
          <Layers size={32} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">
            {searchQuery ? 'No templates match your search' : 'No templates yet'}
          </p>
          <p className="text-xs text-zinc-600 mt-1">
            Save a project as a template from the editor to get started
          </p>
        </div>
      )}
    </div>
  );
}
